import React from "react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import LongForm from "@/components/LongForm";
import { SEO_CONTENT } from "@/data/seoContent";
import { VALUES, CONTACT } from "@/data/site";

const TECH_IMG = "/images/fuitex-van.jpg";
const OFFICE_IMG = "/images/fuitex-office.jpg";

export default function About() {
  return (
    <>
      <Seo
        title="À propos de FuiteX | Expert en Recherche de Fuite d'Eau"
        description="FuiteX, société spécialisée en recherche de fuite d'eau non destructive : voir l'invisible, trouver l'essentiel. Découvrez notre expertise et nos engagements."
        canonical="/a-propos"
      />
      <PageHero
        overline="À propos"
        titleLines={["FuiteX, l'expert de la", "recherche de fuite d'eau"]}
        subtitle="Voir l'invisible, trouver l'essentiel : une mission née d'une conviction — on peut trouver une fuite sans tout casser."
        breadcrumb={[{ label: "À propos" }]}
      />

      <section className="bg-white py-24">
        <div className="container-x grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-7">
            <p className="overline text-aqua mb-4">Notre histoire & notre mission</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-ink leading-tight">
              « Voir l'invisible. Trouver l'essentiel. »
            </h2>
            <p className="mt-5 text-lg text-ink/70 leading-relaxed">
              FuiteX est née d'un constat simple : trop de particuliers et de professionnels subissent
              des travaux inutiles pour retrouver une fuite d'eau. Notre mission est d'inverser cette
              logique — localiser d'abord, réparer ensuite, sans casse superflue.
            </p>
            <p className="mt-4 text-lg text-ink/70 leading-relaxed">
              Spécialisés dans la recherche de fuite non destructive, nous intervenons partout en
              Île-de-France auprès des particuliers, syndics, professionnels du bâtiment et assurances.
              Chaque intervention associe rigueur technique et écoute d'un client souvent stressé.
            </p>

            <h3 className="mt-12 text-2xl font-bold tracking-tight text-ink">Notre expertise & notre matériel</h3>
            <p className="mt-4 text-lg text-ink/70 leading-relaxed">
              Caméras thermiques, détecteurs acoustiques de haute sensibilité, gaz traceur, caméras
              d'inspection : nous investissons dans un équipement professionnel de dernière génération
              pour garantir une localisation précise, quelle que soit la complexité de la fuite.
            </p>

            <h3 className="mt-12 text-2xl font-bold tracking-tight text-ink">Nos engagements</h3>
            <ul className="mt-4 space-y-3 text-lg text-ink/70">
              <li className="flex gap-3"><span className="text-aqua font-bold">→</span> Un compte-rendu détaillé et illustré après chaque intervention.</li>
              <li className="flex gap-3"><span className="text-aqua font-bold">→</span> Des tarifs clairs et un devis gratuit avant toute mission.</li>
              <li className="flex gap-3"><span className="text-aqua font-bold">→</span> Une entreprise assurée, respectueuse de vos locaux.</li>
              <li className="flex gap-3"><span className="text-aqua font-bold">→</span> Une intervention rapide, y compris en situation d'urgence.</li>
            </ul>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="rounded-3xl overflow-hidden border border-ink/10">
              <img src={TECH_IMG} alt="Véhicule FuiteX floqué en intervention à Paris" className="w-full h-auto object-cover" />
            </div>
            <div className="mt-6 rounded-3xl bg-ink p-8 text-white">
              <p className="overline text-aqua mb-3">L'équipe FuiteX</p>
              <p className="text-white/75 leading-relaxed">
                Des techniciens formés à la détection non destructive, disponibles sur
                {" "}{CONTACT.zone.toLowerCase()}, à votre écoute {CONTACT.hours.toLowerCase()}.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white pb-24">
        <div className="container-x">
          <Reveal className="relative overflow-hidden rounded-3xl border border-ink/10">
            <img src={OFFICE_IMG} alt="Bureau et accueil FuiteX, spécialiste recherche de fuite" className="w-full h-[340px] sm:h-[440px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/40 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-end p-8 sm:p-12 max-w-xl">
              <p className="overline text-aqua mb-3">Une entreprise à taille humaine</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white leading-tight">
                Un accueil réactif, une équipe dédiée à votre fuite
              </h2>
              <p className="mt-3 text-white/75">
                Derrière chaque intervention, une équipe qui répond, vous conseille et organise votre rendez-vous rapidement.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist py-24">
        <div className="container-x">
          <Reveal>
            <p className="overline text-aqua mb-4">Nos valeurs</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-ink">Précision, rapidité, transparence, proximité</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07} className="rounded-3xl bg-white border border-ink/10 p-7">
                <span className="text-aqua font-black text-2xl">0{i + 1}</span>
                <h3 className="mt-3 text-xl font-bold text-ink">{v.title}</h3>
                <p className="mt-2 text-sm text-ink/65 leading-relaxed">{v.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24">
        <div className="container-x">
          <LongForm data={SEO_CONTENT.apropos} />
        </div>
      </section>

      <CTASection />
    </>
  );
}
